import React from 'react';
import { Lesson } from "../../../types";
import { Collapse } from "react-collapse";
import Section from "../../Section";
declare const I18n;

type Props = {
  lessons: Lesson[];
  currentLesson: Lesson;
  currentSection: number;
  onLessonClick: (lesson: Lesson, sectionIndex: number) => void;
  showAllSections?: boolean;
  isMiniLesson?: boolean;
  lessonsProgress?: any;
  facilitatorCourseProgress?: any;
  isFacilitator?: boolean;
}

const Sidebar: React.FunctionComponent<Props> = ({
  lessons,
  currentLesson,
  currentSection,
  onLessonClick,
  showAllSections = false,
  isMiniLesson = false,
  lessonsProgress,
  facilitatorCourseProgress,
  isFacilitator
}: Props): JSX.Element => {

  const [openLessons, setOpenLessons] = React.useState<number[]>([]);

  const isSectionCompleted = (lessonId: number, sectionId: number): boolean => {
    if (!lessonsProgress || !Array.isArray(lessonsProgress)) return false;

    return lessonsProgress.some(progress =>
      progress.lesson_id === lessonId &&
      (String(progress.section_id) === String(sectionId) || progress.section_id === "all")
    );
  };

  const isOverviewCompleted = (lessonId: number): boolean => {
    if (!lessonsProgress || !Array.isArray(lessonsProgress)) return false;

    return lessonsProgress.some(progress =>
      progress.lesson_id === lessonId &&
      (progress.section_id === "overview" || progress.section_id === "0" || progress.section_id === "all")
    );
  };

  const isLessonLocked = (lessonIndex: number): boolean => {
    if (isFacilitator) return false;
    if (lessonIndex === 0) return false;
    const previousLesson = lessons[lessonIndex - 1];
    return !previousLesson.completed;
  };

  React.useEffect(() => {
    const currentLessonIndex = lessons.findIndex(l => l.id === currentLesson.id);

    if (isMiniLesson) {
      setOpenLessons(prev => (prev.length ? prev : lessons.map((_, i) => i)));
      return;
    }

    if (currentLessonIndex > 0) {
      setOpenLessons(prev => (prev.includes(currentLessonIndex) ? prev : [...prev, currentLessonIndex]));
    }
  }, [currentLesson.id, lessons, isMiniLesson]);

  React.useEffect(() => {
    const currentLessonIndex = lessons.findIndex(l => l.id === currentLesson.id);
    if (currentLessonIndex > 0 && !openLessons.includes(currentLessonIndex)) {
      setOpenLessons([...openLessons, currentLessonIndex]);
    }
  }, [currentLesson.id, lessons, openLessons, lessonsProgress]);

  const handleLessonClick = (lesson: Lesson, sectionIndex: number, lessonIndex: number) => {
    if (isLessonLocked(lessonIndex)) return;
    onLessonClick(lesson, sectionIndex);
  };

  const toggleLesson = (lessonIndex: number) => {
    if (openLessons.includes(lessonIndex)) {
      setOpenLessons(openLessons.filter(i => i !== lessonIndex));
    } else {
      setOpenLessons([...openLessons, lessonIndex]);
    }
  };

  const handleLessonKeyPress = (event: React.KeyboardEvent, lessonIndex: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleLesson(lessonIndex);
    }
  };

  const renderLesson = (lesson: Lesson, lessonIndex: number) => {
    const isFirstLesson = lessonIndex === 0;
    const isCurrentLesson = lesson.id === currentLesson.id;
    const isLocked = !isFacilitator && !isCurrentLesson && isLessonLocked(lessonIndex);
    const isCollapsed = !openLessons.includes(lessonIndex);
    // For regular courses AND first lesson, show as "Overview" (non-collapsible)
    if (isFirstLesson && !isMiniLesson) {
      const isActive = isCurrentLesson && (showAllSections ? true : currentSection === 0);
      return (
        <button
          key={lesson.id}
          className={`sidebar-lesson__lesson overview ${isActive ? 'sidebar-lesson__lesson--active' : ''}`}
          onClick={() => handleLessonClick(lesson, 0, lessonIndex)}
          style={{ cursor: 'pointer' }}
        >
          <Section
            title={I18n.t("views.common.overview")}
            withBorder={false}
            index={lessonIndex}
            iconId={lesson.id}
          />
        </button>
      );
    }

    // All other lessons are collapsible (including first lesson for mini-lessons)
    return (
      <nav
        aria-label="Course Navigation"
        key={lesson.id}
        className={`sidebar-lesson__lesson ${isLocked ? 'sidebar-lesson__lesson--locked' : ''} ${isCurrentLesson ? 'sidebar-lesson__lesson--active' : ''}`}
        style={{ width: '100%' }}
      >
        <ol className="sidebar-ol">
          <button
            className={`sidebar-lesson__lesson_title ${isCurrentLesson ? 'sidebar-lesson__lesson_title--active' : ''}`}
            onClick={() => toggleLesson(lessonIndex)}
            onKeyDown={(e) => handleLessonKeyPress(e, lessonIndex)}
            tabIndex={0}
            aria-expanded={!isCollapsed}
            aria-controls={`lesson-content-${lessonIndex}`}
          >
            {isMiniLesson ? (
              lessons.length > 1 ? (
                <div className="sidebar-lesson__lesson_number">
                  <span>{lessonIndex + 1}</span>
                </div>
              ) : null
            ) : lesson.completed ? (
              <i className="fa-solid fa-circle-check" />
            ) : isLocked ? (
              <i className="fa-solid fa-lock" />
            ) : (
              <div className="sidebar-lesson__lesson_number">
                <span>{isFirstLesson ? 1 : lessonIndex}</span>
              </div>
            )}
            <Section
              title={lesson.title}
              withBorder={true}
              index={lessonIndex}
              iconId={lesson.id}
              onArrowPress={() => toggleLesson(lessonIndex)}
              style={{ cursor: 'pointer', opacity: isLocked ? 0.5 : 1 }}
              headingLevel='h3'
            />
          </button>

          <Collapse
            isOpened={!isCollapsed}
            id={`lesson-content-${lessonIndex}`}
          >
            <div className="sidebar-lesson__sections">
              {/* Only show Overview subsection for regular courses (NOT mini-lessons) AND NOT for the first lesson */}
              {!isMiniLesson && !isFirstLesson && (
                <button
                  className={`sidebar-lesson__section overview ${isCurrentLesson && currentSection === 0 ? 'sidebar__section--active' : ''}`}
                  onClick={() => handleLessonClick(lesson, 0, lessonIndex)}
                  style={{
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    paddingLeft: '1rem',
                    opacity: isLocked ? 0.5 : 1
                  }}
                  tabIndex={isCollapsed ? -1 : 0}
                >
                  {lesson.completed ?
                    <i className="fa-solid fa-circle-check" /> :
                    isOverviewCompleted(lesson.id) ?
                      <i className="fa-solid fa-circle-check" /> :
                      <div className="sidebar-lesson__lesson_number indented-left">
                        <span>{1}</span>
                      </div>
                  }
                  {I18n.t("views.common.overview")}
                </button>
              )}

              {/* All sections */}
              {lesson.sections && lesson.sections.map((section, sectionIndex) => {
                const actualSectionIndex = sectionIndex + 1;
                const isCurrentSection = isCurrentLesson && currentSection === actualSectionIndex;
                const sectionCompleted = isSectionCompleted(lesson.id, section.id);

                let sectionNumber;
                if (isMiniLesson) {
                  sectionNumber = sectionIndex + 1;
                } else if (isFirstLesson) {
                  sectionNumber = actualSectionIndex;
                } else {
                  sectionNumber = actualSectionIndex + 1;
                }

                return (
                  <button
                    key={section.id}
                    className={`sidebar-lesson__section ${isCurrentSection ? 'sidebar__section--active' : ''}`}
                    onClick={() => handleLessonClick(lesson, actualSectionIndex, lessonIndex)}
                    style={{
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      paddingLeft: '1rem',
                      opacity: isLocked ? 0.5 : 1
                    }}
                    tabIndex={isCollapsed ? -1 : 0}
                    aria-controls={`lesson-content-${lessonIndex}`}
                  >
                    {isMiniLesson && lesson.sections?.length === 1 ? null : (
                      lesson.completed ? (
                        <i className="fa-solid fa-circle-check" />
                      ) : sectionCompleted ? (
                        <i className="fa-solid fa-circle-check" />
                      ) : (
                        <div className="sidebar-lesson__lesson_number">
                          <span>{sectionNumber}</span>
                        </div>
                      )
                    )}
                    {section.title}
                  </button>
                );
              })}
            </div>
          </Collapse>
        </ol>
      </nav>
    );
  };

  return (
    <aside className="sidebar-lesson">
      {lessons.map((lesson, lessonIndex) => renderLesson(lesson, lessonIndex))}
    </aside>
  );
}

export default Sidebar;